import { inject } from '@angular/core';
import { SupportedMetamodels } from '@bizzdesign/modeler/models';
import { imagesTrackEvents } from '@bizzdesign/modeler/utils-canvazz/browser';
import {
  contextMenuUiEvents,
  graphActionUiEvents,
  groupUiEvents,
  stickyAuthorUiEvents,
  styleElementUiEvents,
  swapElementUiEvents,
  zOrderUiEvents,
} from '@bizzdesign/modeler/utils-canvazz/events';
import { cursorUiEvents } from '@bizzdesign/modeler/utils-collaboration/browser';
import { type ProductEvents } from '@bizzdesign/shared/models';
import { ProductAnalyticsService } from '@bizzdesign/shared/services';
import { signalStoreFeature } from '@ngrx/signals';
import {
  type EventCreator,
  Events,
  withEventHandlers,
} from '@ngrx/signals/events';
import { tap } from 'rxjs';
import { canvasObjectUiEvents } from '../../events/canvas-object/canvas-object.events';
import { colorViewUiEvents } from '../../events/color-view/color-view.events';
import { dialogPublishSuccessUiEvents } from '../../events/dialog/dialog-publish-success.events';
import { dialogPublishUiEvents } from '../../events/dialog/dialog-publish.events';
import { dialogReuseUiEvents } from '../../events/dialog/dialog-reuse.events';
import { dialogWorkspacePickerUiEvents } from '../../events/dialog/dialog-workspace-picker.event';
import { modelingAssistanceUiEvents } from '../../events/modeling-assistance/modeling-assistance.event';
import { frameViewControlToolbarUiEvents } from '../../events/toolbar/toolbar-view-control.events';
import { graphActionToolbarUiEvents } from '../../events/toolbar/top-toolbar.events';
import { sendAnalyticsEvent } from './analytics-effects.utils';
import { withElementAnalyticsEffect } from './with-element-analytics-effects.feature';

export function withAnalyticsEffects() {
  return signalStoreFeature(
    withEventHandlers(() => {
      const events = inject(Events);
      const analyticsService = inject(ProductAnalyticsService);

      const trackVoidEvent = <Payload>(
        event: EventCreator<string, Payload>,
        eventName: ProductEvents,
      ) =>
        events.on(event).pipe(tap(() => analyticsService.sendEvent(eventName)));

      const sendGeneralAnalyticsEvent = (
        eventName: ProductEvents,
        payload?: Record<string, unknown>,
      ) => {
        sendAnalyticsEvent(analyticsService, eventName, payload);
      };

      return {
        addToFrameClicked$: trackVoidEvent(
          graphActionToolbarUiEvents.addToFrameClicked,
          'add_to_frame_clicked',
        ),
        addToFrameKeyboardTriggered$: events
          .on(graphActionUiEvents.addToFrameTriggered)
          .pipe(
            tap(({ payload: { triggered_from_keyboard } }) =>
              sendGeneralAnalyticsEvent('add_to_frame_clicked', {
                triggered_from_keyboard,
              }),
            ),
          ),
        workspacePickerNewWorkspaceClicked$: trackVoidEvent(
          dialogWorkspacePickerUiEvents.newWorkspaceClicked,
          'workspace_picker_new_workspace_clicked',
        ),
        workspacePickerConfirmClicked$: trackVoidEvent(
          dialogWorkspacePickerUiEvents.workspaceConfirmClicked,
          'workspace_picker_confirm_clicked',
        ),
        autoLayoutTriggered$: trackVoidEvent(
          frameViewControlToolbarUiEvents.autolayoutTriggered,
          'autolayout_triggered',
        ),
        viewModeEnabled$: trackVoidEvent(
          frameViewControlToolbarUiEvents.viewModeEnabled,
          'frame_view_mode_on',
        ),
        viewModeDisabled$: trackVoidEvent(
          frameViewControlToolbarUiEvents.viewModeDisabled,
          'frame_view_mode_off',
        ),
        zoomLevelChanged$: events
          .on(frameViewControlToolbarUiEvents.zoomLevelChanged)
          .pipe(
            tap(({ payload: { zoom, location } }) => {
              sendGeneralAnalyticsEvent('frame_zoom_level_changed', {
                zoom,
                location,
              });
            }),
          ),
        fitToScreenTriggered$: events
          .on(frameViewControlToolbarUiEvents.fitToScreenTriggered)
          .pipe(
            tap(({ payload: { location } }) => {
              sendGeneralAnalyticsEvent('frame_fit_to_screen_triggered', {
                location,
              });
            }),
          ),
        fullScreenToggled$: events
          .on(frameViewControlToolbarUiEvents.fullScreenToggled)
          .pipe(
            tap(({ payload: { location, status } }) => {
              sendGeneralAnalyticsEvent(
                status === 'on'
                  ? 'frame_fullscreen_on'
                  : 'frame_fullscreen_off',
                { location },
              );
            }),
          ),
        relatedObjectsChecked$: events
          .on(contextMenuUiEvents.relatedObjectsChecked)
          .pipe(
            tap(({ payload: objects_count }) =>
              sendGeneralAnalyticsEvent('related_objects_checked', {
                objects_count,
              }),
            ),
          ),
        redirectDialogViewOpened$: events
          .on(dialogPublishSuccessUiEvents.viewOpened)
          .pipe(
            tap(() => sendGeneralAnalyticsEvent('redirect_dialog_view_opened')),
          ),
        redirectDialogFrameOpened$: trackVoidEvent(
          dialogPublishSuccessUiEvents.dialogClosed,
          'redirect_dialog_frame_opened',
        ),
        relatedObjectsLoaded$: trackVoidEvent(
          contextMenuUiEvents.relatedObjectsLoaded,
          'related_objects_loaded',
        ),
        toggleModelingAssistancePanel$: events
          .on(modelingAssistanceUiEvents.modelingAssistancePanelToggled)
          .pipe(
            tap(({ payload: { location, isOpen } }) => {
              sendGeneralAnalyticsEvent(
                isOpen
                  ? 'modeling_assistance_panel_opened'
                  : 'modeling_assistance_panel_closed',
                { location },
              );
            }),
          ),
        toggleModelingAssistance$: events
          .on(modelingAssistanceUiEvents.modelingAssistanceToggled)
          .pipe(
            tap(({ payload: { location, targetMetamodel } }) => {
              sendGeneralAnalyticsEvent(
                targetMetamodel === undefined ||
                  targetMetamodel === SupportedMetamodels.FREEFORMAT
                  ? 'modeling_assistance_off'
                  : 'modeling_assistance_on',
                { location },
              );
            }),
          ),
        reuseDialogOpened$: events
          .on(dialogReuseUiEvents.dialogOpened)
          .pipe(tap(() => sendGeneralAnalyticsEvent('reuse_dialog_opened'))),
        reuseConfirmClicked$: trackVoidEvent(
          modelingAssistanceUiEvents.reuseConfirmClicked,
          'reuse_confirm_clicked',
        ),
        highlightMenuClicked$: trackVoidEvent(
          colorViewUiEvents.highlightMenuClicked,
          'highlight_menu_clicked',
        ),
        highlightAttributeSelected$: trackVoidEvent(
          colorViewUiEvents.highlightAttributeSelected,
          'highlight_attribute_selected',
        ),
        colorviewLegendColorChanged$: trackVoidEvent(
          colorViewUiEvents.colorviewLegendColorChanged,
          'colorview_legend_color_changed',
        ),
        colorviewTurnedOff$: trackVoidEvent(
          colorViewUiEvents.colorviewTurnedOff,
          'colorview_turned_off',
        ),
        colorviewLegendCollapsed$: trackVoidEvent(
          colorViewUiEvents.colorviewLegendCollapsed,
          'colorview_legend_collapsed',
        ),
        colorviewLegendExpanded$: trackVoidEvent(
          colorViewUiEvents.colorviewLegendExpanded,
          'colorview_legend_expanded',
        ),
        freeFormToBdDraftSwapped$: trackVoidEvent(
          swapElementUiEvents.freeFormToBdDraftSwapped,
          'freeform_to_bddraft_swapped',
        ),
        bdDraftToFreeFormSwapped$: trackVoidEvent(
          swapElementUiEvents.bdDraftToFreeFormSwapped,
          'bddraft_to_freeform_swapped',
        ),
        freeFormToFromRepoSwapped$: trackVoidEvent(
          swapElementUiEvents.freeFormToFromRepoSwapped,
          'freeform_to_fromrepo_swapped',
        ),
        bdDraftToFromRepoSwapped$: trackVoidEvent(
          swapElementUiEvents.bdDraftToFromRepoSwapped,
          'bddraft_to_fromrepo_swapped',
        ),
        lineToBdRelationSwapped$: trackVoidEvent(
          swapElementUiEvents.lineToBdRelationSwapped,
          'line_to_bdrelation_swapped',
        ),
        bdRelationToLineSwapped$: trackVoidEvent(
          swapElementUiEvents.bdRelationToLineSwapped,
          'bdrelation_to_line_swapped',
        ),
        hideCollabCursors$: trackVoidEvent(
          cursorUiEvents.hideCursors,
          'collab_cursors_hidden',
        ),
        showCollabCursors$: trackVoidEvent(
          cursorUiEvents.showCursors,
          'collab_cursors_shown',
        ),
        elementColorChanged$: events.on(styleElementUiEvents.colorChanged).pipe(
          tap(({ payload: { color } }) =>
            sendGeneralAnalyticsEvent('element_color_changed', {
              color,
            }),
          ),
        ),
        elementFontTypeChanged$: events
          .on(styleElementUiEvents.fontTypeChanged)
          .pipe(
            tap(({ payload: { font } }) =>
              sendGeneralAnalyticsEvent('element_font_type_changed', {
                font,
              }),
            ),
          ),
        elementFontSizeChanged$: events
          .on(styleElementUiEvents.fontSizeChanged)
          .pipe(
            tap(({ payload: { size } }) =>
              sendGeneralAnalyticsEvent('element_font_size_changed', {
                size,
              }),
            ),
          ),
        elementFontBold$: events.on(styleElementUiEvents.fontBoldChanged).pipe(
          tap(({ payload: { status, triggered_from_keyboard } }) =>
            sendGeneralAnalyticsEvent('element_font_bold_changed', {
              status,
              triggered_from_keyboard,
            }),
          ),
        ),
        elementFontItalic$: events
          .on(styleElementUiEvents.fontItalicChanged)
          .pipe(
            tap(({ payload: { status, triggered_from_keyboard } }) =>
              sendGeneralAnalyticsEvent('element_font_italic_changed', {
                status,
                triggered_from_keyboard,
              }),
            ),
          ),
        elementFontUnderline$: events
          .on(styleElementUiEvents.fontUnderlineChanged)
          .pipe(
            tap(({ payload: { status, triggered_from_keyboard } }) =>
              sendGeneralAnalyticsEvent('element_font_underline_changed', {
                status,
                triggered_from_keyboard,
              }),
            ),
          ),
        elementFontStrikethrough$: events
          .on(styleElementUiEvents.fontStrikethroughChanged)
          .pipe(
            tap(({ payload: { status, triggered_from_keyboard } }) =>
              sendGeneralAnalyticsEvent('element_font_strikethrough_changed', {
                status,
                triggered_from_keyboard,
              }),
            ),
          ),
        elementHorizontalAlignmentChanged$: events
          .on(styleElementUiEvents.horizontalAlignmentChanged)
          .pipe(
            tap(({ payload: { direction, elementType } }) =>
              sendGeneralAnalyticsEvent(
                'element_horizontal_alignment_changed',
                {
                  direction,
                  elementType,
                },
              ),
            ),
          ),
        elementVerticalAlignmentChanged$: events
          .on(styleElementUiEvents.verticalAlignmentChanged)
          .pipe(
            tap(({ payload: { direction, elementType } }) =>
              sendGeneralAnalyticsEvent('element_vertical_alignment_changed', {
                direction,
                elementType,
              }),
            ),
          ),
        publishDialogOpened$: events
          .on(dialogPublishUiEvents.dialogOpened)
          .pipe(
            tap(({ payload: { location } }) => {
              sendGeneralAnalyticsEvent('publish_dialog_opened', { location });
            }),
          ),
        publishConfirmClicked$: events.on(dialogPublishUiEvents.submitted).pipe(
          tap(({ payload: { isRepublishing, publishMode } }) => {
            sendGeneralAnalyticsEvent('publish_confirm_clicked', {
              publish_mode:
                publishMode ?? (isRepublishing ? 'existing_view' : 'new_view'),
            });
          }),
        ),
        publishingSeeConflictsTriggered$: trackVoidEvent(
          dialogPublishUiEvents.seeConflictsClicked,
          'publishing_see_conflicts_triggered',
        ),
        publishingSeeElementsTriggered$: trackVoidEvent(
          dialogPublishUiEvents.seeElementsClicked,
          'publishing_see_elements_triggered',
        ),
        generateViewDescriptionClicked$: events
          .on(dialogPublishUiEvents.generateViewDescriptionClicked)
          .pipe(
            tap(({ payload: { location } }) =>
              sendGeneralAnalyticsEvent('generate_view_description_clicked', {
                location,
              }),
            ),
          ),
        shortcutsDialogOpened$: trackVoidEvent(
          frameViewControlToolbarUiEvents.shortcutsTriggered,
          'shortcuts_dialog_opened',
        ),
        fromRepoObjectAddedToCanvas$: events
          .on(canvasObjectUiEvents.fromRepoObjectAddedToCanvas)
          .pipe(
            tap(({ payload: { interaction_type, location, type } }) => {
              sendGeneralAnalyticsEvent('from_repo_object_added_to_canvas', {
                interaction_type,
                location,
                type,
              });
            }),
          ),
        draftObjectAddedToCanvas$: events
          .on(canvasObjectUiEvents.draftObjectAddedToCanvas)
          .pipe(
            tap(
              ({
                payload: {
                  interaction_type,
                  location,
                  metamodel,
                  shapeType,
                  promotionType,
                },
              }) => {
                sendGeneralAnalyticsEvent('draft_object_added_to_canvas', {
                  interaction_type,
                  location,
                  metamodel,
                  shapeType,
                  promotionType,
                });
              },
            ),
          ),
        stickyAuthorVisibilityToggled$: events
          .on(stickyAuthorUiEvents.stickyAuthorVisibilityToggled)
          .pipe(
            tap(({ payload: { location, status } }) =>
              sendGeneralAnalyticsEvent('sticky_author_visibility_toggled', {
                location,
                status,
              }),
            ),
          ),
        bringToFrontTriggered$: events
          .on(zOrderUiEvents.bringToFrontTriggered)
          .pipe(
            tap(
              ({
                payload: {
                  location,
                  commandStatus: command_status,
                  triggered_from_keyboard,
                },
              }) =>
                sendGeneralAnalyticsEvent('bring_to_front_triggered', {
                  location: triggered_from_keyboard ? undefined : location,
                  command_status,
                  triggered_from_keyboard,
                }),
            ),
          ),
        bringForwardTriggered$: events
          .on(zOrderUiEvents.bringForwardTriggered)
          .pipe(
            tap(
              ({
                payload: {
                  location,
                  commandStatus: command_status,
                  triggered_from_keyboard,
                },
              }) =>
                sendGeneralAnalyticsEvent('bring_forward_triggered', {
                  location: triggered_from_keyboard ? undefined : location,
                  command_status,
                  triggered_from_keyboard,
                }),
            ),
          ),
        sendBackwardTriggered$: events
          .on(zOrderUiEvents.sendBackwardTriggered)
          .pipe(
            tap(
              ({
                payload: {
                  location,
                  commandStatus: command_status,
                  triggered_from_keyboard,
                },
              }) =>
                sendGeneralAnalyticsEvent('send_backward_triggered', {
                  location: triggered_from_keyboard ? undefined : location,
                  command_status,
                  triggered_from_keyboard,
                }),
            ),
          ),
        sendToBackTriggered$: events
          .on(zOrderUiEvents.sendToBackTriggered)
          .pipe(
            tap(
              ({
                payload: {
                  location,
                  commandStatus: command_status,
                  triggered_from_keyboard,
                },
              }) =>
                sendGeneralAnalyticsEvent('send_to_back_triggered', {
                  location: triggered_from_keyboard ? undefined : location,
                  command_status,
                  triggered_from_keyboard,
                }),
            ),
          ),
        groupTriggered$: events.on(groupUiEvents.groupTriggered).pipe(
          tap(
            ({
              payload: {
                commandStatus: command_status,
                triggered_from_keyboard,
                location,
              },
            }) =>
              sendGeneralAnalyticsEvent('group_triggered', {
                command_status,
                triggered_from_keyboard,
                location,
              }),
          ),
        ),
        ungroupTriggered$: events.on(groupUiEvents.ungroupTriggered).pipe(
          tap(
            ({
              payload: {
                commandStatus: command_status,
                triggered_from_keyboard,
                location,
              },
            }) =>
              sendGeneralAnalyticsEvent('ungroup_triggered', {
                command_status,
                triggered_from_keyboard,
                location,
              }),
          ),
        ),
        imagesPastedOnFrame$: events.on(imagesTrackEvents.imagesPasted).pipe(
          tap(({ payload: { location, imageTypes: image_types } }) =>
            sendGeneralAnalyticsEvent('images_pasted_on_frame', {
              location,
              image_types,
            }),
          ),
        ),
        imagesDroppedOnFrame$: events.on(imagesTrackEvents.imagesDropped).pipe(
          tap(({ payload: { imageTypes: image_types } }) =>
            sendGeneralAnalyticsEvent('images_dropped_on_frame', {
              image_types,
            }),
          ),
        ),
      };
    }),
    withElementAnalyticsEffect(),
  );
}
